import { resolveMatchedBranch, evaluateEquation } from './conditional-branch.js';
import { isCompassWithinAcceptedRange } from './compass.js';
import { buildScaleValueTrigger } from './scale.js';
import { computeAcceptedRanges, isWithinAcceptedRange, uncertaintyAsked, uncertaintyBand, uncertaintyMatches } from './numerical.js';
import { haversineDistanceMeters } from './gps.js';
import { cleanComputed } from './epsilon.js';
import { answerToleranceMode, answerHalfWidths } from './answer-tolerance.js';
import { bucketIn, bucketSpan, valueInBucket } from './attempt-signature.js';
import { resolveScenarioOptions } from './scenario.js';
import { isSliderWithinAcceptedRange } from './slider.js';
import { TILT_DOMAIN_HI, TILT_DOMAIN_LO, isTiltWithinAcceptedRange } from './tilt.js';
const asNumber = (v) => (typeof v === 'number' && Number.isFinite(v) ? v
    : typeof v === 'string' && v.trim() !== '' && Number.isFinite(Number(v)) ? Number(v)
        : null);
/**
 * A value field that may be an equation.
 *
 * Numerical, Slider and the sensors all accept a formula where a number goes, so a sample has
 * to evaluate before it can report. A plain number short-circuits, which also means a block
 * with no variables never touches the evaluator.
 */
function valueOf(raw, ctx) {
    const direct = asNumber(raw);
    if (direct !== null)
        return direct;
    if (typeof raw !== 'string' || raw.trim() === '')
        return null;
    const computed = evaluateEquation(raw, ctx.variables, ctx.angleMode ?? 'degrees');
    /*
     * OUR OWN ARITHMETIC, so it is cleaned — the rule `cleanComputed` states and this path was
     * missing. `{river}+{kayak}` on a roll of 2.3 and 1.4 is 3.6999999999999997, and a reference
     * quoting the answer printed all seventeen digits into a sentence.
     *
     * Only this branch. A literal above is what the TEACHER typed, and the same rule says a typed
     * value is left exactly as given; rounding it here would silently restate an authored
     * thirteen-digit bound as a twelve-digit one.
     */
    return computed === null ? null : cleanComputed(computed);
}
/** The first authored answer of a block that keeps them in an `answers` array. */
function firstAnswerValue(data, ctx) {
    const answers = data.answers;
    if (!Array.isArray(answers) || answers.length === 0)
        return null;
    return valueOf(answers[0]?.value, ctx);
}
function resolvedEntries(data, key, ctx) {
    const list = data[key];
    if (!Array.isArray(list))
        return [];
    const angle = ctx.angleMode ?? 'degrees';
    /*
     * Scale's points have no id, and the key its branches use is not the bare value either —
     * `buildScaleValueTrigger` wraps it, and that wrapper is the only thing a role map will
     * match. Deriving `String(value)` instead looks right, resolves nothing, and silently
     * reports every point as the teacher authored it however the roll falls.
     */
    const withIds = list.map(raw => {
        const e = raw;
        const id = typeof e.id === 'string' && e.id !== ''
            ? e.id
            : buildScaleValueTrigger(Number(e.value));
        return { ...raw, id };
    });
    const scoped = resolveScenarioOptions(withIds, data.scenarioBranches, ctx.scenarioId ?? firstScenarioId(data), ctx.variables, angle);
    const branch = resolveMatchedBranch(data.conditionalEquation, data.conditionalBranches, ctx.variables, angle);
    const roles = branch?.roles;
    return roles ? scoped.map(e => ({ ...e, role: roles[e.id] ?? e.role })) : scoped;
}
/**
 * DOES THIS BLOCK AUTHOR A CORRECT ANSWER AT ALL — the line between two states that look
 * identical after resolution and mean opposite things.
 *
 * A SURVEY authors none, so there is no right answer to miss and any entry stands in. A
 * block whose correctness is decided by a SCENARIO or a BRANCH authors one and can have it
 * taken away for a draw it has no row for — and there, standing in with a different entry is
 * the silent wrong answer this module exists to avoid.
 */
function authorsCorrect(data, key) {
    const list = data[key];
    return Array.isArray(list) && list.some(e => e?.role === 'correct');
}
/**
 * AND A DISTRACTOR NEVER STANDS IN.
 *
 * An entry marked `incorrect` is one the teacher wrote to be wrong. Offering it as the
 * sample would put a wrong answer in the sentence with nothing saying so — worse than the
 * silence this fallback exists to end. Pinned by "nothing for a blank whose words are all
 * distractors", which the first version of the fallback broke.
 *
 * A survey's entries carry no role, or `neutral`. Those are the ones that may stand in.
 */
const mayStandIn = (e) => e.role === undefined || e.role === null || e.role === 'neutral';
/** Just the correct ones. */
const correctEntries = (data, key, ctx) => resolvedEntries(data, key, ctx).filter(e => e.role === 'correct');
/**
 * Those entries' text, for the blocks whose answer IS the text.
 *
 * Blank is judged on what the text SAYS, not on what it stores: a row the teacher started and
 * left is `'  '` on Keyword and `'<p></p>'` on an option, and sampling either puts an
 * invisible answer in the middle of the sentence the sample exists to let them read.
 */
const textsOf = (entries) => entries
    .map(e => String(e.text ?? ''))
    .filter(text => text.replace(/<[^>]*>/g, '').trim() !== '');
/**
 * THE ROW A FOLLOWER IS SAMPLED AGAINST when nothing says which was drawn.
 *
 * A caller in a session knows the leader's draw. The editor's test card does not: the leader
 * is in another task, and that task's roll is not this one's. Grading fails closed there —
 * `resolveScenarioOptions` marks every option neutral rather than reach for the block's own
 * unscoped key — and that is right for grading and useless here, because a follower would then
 * be the one kind of block a sample could never speak for. In Physics Test 1 that is three
 * references out of four.
 *
 * So a sample takes the first row, and says so. It is the same bargain the rest of this module
 * makes: representative, not a prediction. A teacher who wants the other variant asks for it.
 */
function firstScenarioId(data) {
    const rows = data.scenarioBranches;
    if (!Array.isArray(rows) || rows.length === 0)
        return undefined;
    const id = rows[0]?.scenarioId;
    return typeof id === 'string' ? id : undefined;
}
/**
 * The first word a FITB blank accepts.
 *
 * FITB's own, rather than `correctEntries`: its roles are per BLANK, and its branches key a
 * flat `Record<wordId, role>` across every blank at once, so the per-list resolution the
 * option blocks share does not describe it. A sample takes the authored role.
 */
function firstCorrectWord(list) {
    if (!Array.isArray(list))
        return null;
    const words = list;
    const say = (w) => (typeof w?.text === 'string' && w.text.trim() !== '' ? w.text : null);
    const correct = say(words.find(o => o?.role === 'correct'));
    if (correct !== null)
        return correct;
    /* A BLANK WITH NO CORRECT WORD is a survey blank, and its first authored word stands in —
       the same rule the option blocks follow. Where one IS authored correct and did not come
       back, the blank has nothing honest to show and says nothing. */
    if (words.some(o => o?.role === 'correct'))
        return null;
    for (const w of words) {
        if (!mayStandIn(w))
            continue;
        const text = say(w);
        if (text !== null)
            return text;
    }
    return null;
}
export function sampleCorrectAnswer(blockType, data, ctx) {
    switch (blockType) {
        case 'keyword': {
            /*
             * `options`, and through the same branch resolution as MCQ — Keyword carries both
             * `conditionalBranches` and `scenarioBranches`, so which word is accepted moves with the
             * roll exactly as an option's role does. It RECORDS a bare string, so that is what a
             * sample is.
             */
            const texts = textsOf(correctEntries(data, 'options', ctx));
            if (texts.length > 0)
                return texts[0];
            /* A SURVEY KEYWORD accepts anything, so its first authored word stands in — the same
               rule as an option block, and the same reason: a chip quoting it otherwise stayed a
               chip among resolved neighbours. */
            if (authorsCorrect(data, 'options'))
                return null;
            const any = textsOf(resolvedEntries(data, 'options', ctx).filter(mayStandIn));
            return any.length > 0 ? any[0] : null;
        }
        case 'scale': {
            const correct = correctEntries(data, 'values', ctx);
            if (correct.length > 0)
                return asNumber(correct[0].value);
            /*
             * AND A LIKERT IS THE CANONICAL SURVEY, so this is the block the gap hurt most: a
             * scale with no correct point marked — which is most of them — sampled nothing, and
             * every chip quoting one stayed a chip.
             *
             * The FIRST point, not the middle. It is as arbitrary a choice as any on a scale with
             * no right answer, and "the first authored entry" is one rule across every block that
             * has entries rather than a second rule that only scales follow.
             */
            if (authorsCorrect(data, 'values'))
                return null;
            const first = resolvedEntries(data, 'values', ctx)
                .filter(mayStandIn).map(e => asNumber(e.value)).find(v => v !== null);
            return first ?? null;
        }
        case 'tilt':
            return firstAnswerValue(data, ctx);
        case 'compass': {
            const heading = firstAnswerValue(data, ctx);
            return heading === null ? null : { headingDegrees: heading };
        }
        case 'gps': {
            const lat = valueOf(data.targetLat, ctx);
            const lng = valueOf(data.targetLng, ctx);
            return lat === null || lng === null ? null : { lat, lng };
        }
        case 'numerical': {
            const value = firstAnswerValue(data, ctx);
            if (value === null)
                return null;
            /*
             * THE UNIT TOO, which this left out at first on the grounds that a student picks one
             * spelling among many accepted ones and a sample cannot know which. True, and beside
             * the point: a block authored `withUnits` has two more facets, and leaving the unit out
             * made both of them render nothing — a reference to the unit alone was blank, and the
             * combined facet quietly showed the bare number, which is the one thing "combined"
             * promises not to be.
             *
             * The canonical spelling is the one the teacher typed, so it is the honest stand-in.
             */
            /*
             * AND THE UNCERTAINTY, through `withUncertainty` — which every WRONG sample uses too,
             * so a chip naming the error box keeps resolving as the teacher cycles the tiers.
             */
            return withUncertainty(withUnit(value, data), data, ctx);
        }
        case 'slider': {
            const answers = data.answers;
            if (!Array.isArray(answers) || answers.length === 0)
                return null;
            const low = valueOf(answers[0]?.value, ctx);
            if (low === null)
                return null;
            /*
             * `handleB`, not `answers[1]`. A dual slider's second handle is a whole answer setup of
             * its own — its own condition, its own tolerances — and nothing is ever written into a
             * second entry of the first handle's list. Reading one there found `undefined` every
             * time, so a dual slider's `high` facet resolved to nothing and its `combined` facet,
             * which needs both ends, resolved to nothing with it.
             *
             * ALWAYS the pair, as the block submits — `resolveAnswerRef` ignores `b` on one handle.
             */
            const handleB = data.handleB;
            const high = data.dualHandle && Array.isArray(handleB?.answers)
                ? valueOf(handleB.answers[0]?.value, ctx)
                : null;
            return { a: { value: low }, b: high === null ? undefined : { value: high } };
        }
        case 'fitb': {
            const blanks = data.blanks;
            if (!Array.isArray(blanks))
                return null;
            const out = {};
            for (const blank of blanks) {
                const id = blank?.id;
                if (typeof id !== 'string')
                    continue;
                // The facet IS the blank id, so every blank has to be filled or a chip naming one of
                // them samples empty while its neighbours resolve.
                const text = firstCorrectWord(blank.words);
                if (text !== null)
                    out[id] = text;
            }
            return Object.keys(out).length > 0 ? out : null;
        }
        case 'mcq':
        case 'dropdown': {
            /*
             * WHERE NOTHING IS CORRECT, ANY OPTION STANDS IN — and "correct" is vacuous, not absent.
             *
             * A survey or participation block marks no option correct, so `correctEntries` was
             * empty, the sample was null, and a chip quoting it never resolved: it stayed a chip in
             * the test card and in Preview while every other reference around it read as a value.
             * Reported as "mcq ... not resolving in the testing block, they are still showing as
             * chips".
             *
             * The first authored option, because a block with no right answer has no better
             * candidate and showing nothing is strictly worse than showing one of the things a
             * student could have picked. The picker still offers every option, and the verdict mark
             * beside the value says `neutral` rather than claiming it is right.
             */
            const correct = textsOf(correctEntries(data, 'options', ctx));
            /* See `authorsCorrect` for why a survey stands in and a follower does not. */
            const texts = correct.length > 0 || authorsCorrect(data, 'options')
                ? correct
                : textsOf(resolvedEntries(data, 'options', ctx).filter(mayStandIn)).slice(0, 1);
            // The same key the log holds, and an array even for one: `resolveAnswerRef` joins a
            // multi-select itself and a single selection is the one-element case of that.
            return texts.length > 0 ? { selectedTexts: texts } : null;
        }
        default:
            return null;
    }
}
/**
 * A NUMERICAL ANSWER AS THE BLOCK RECORDS IT, unit and all.
 *
 * Shared because BOTH samples need it and only one had it: the correct answer carried the
 * unit and the wrong one did not, so cycling a `withUnits` reference went from "10 m" to a
 * bare number and the sentence lost a word. A unit is a property of the quantity, not of
 * being right about it.
 *
 * The canonical spelling is the one the teacher typed, so it is the honest stand-in for a
 * choice among accepted ones that a sample cannot otherwise make.
 */
function withUnit(value, data) {
    if (data.unitMode !== 'withUnits')
        return { value };
    const unit = typeof data.canonicalUnit === 'string' && data.canonicalUnit.trim() !== ''
        ? data.canonicalUnit
        : (Array.isArray(data.unitOptions)
            ? data.unitOptions
                .find(o => o.role === 'correct' || o.role === undefined)?.label
            : undefined);
    return typeof unit === 'string' && unit.trim() !== '' ? { value, unit } : { value };
}
/**
 * THE CORRECT UNCERTAINTY, attached to whatever numerical sample is being built.
 *
 * Shared by the correct sample and every WRONG one, which is the half that was missing and
 * the reason a chip went blank mid-cycle: `sampleCorrectAnswer` carried an uncertainty and
 * `wrongAnswerFor` did not, so stepping an `uncertainty` chip off its first tier produced a
 * sample with no uncertainty in it at all. The facet then resolved to nothing and the chip
 * came back — "when i click on uncertainty, it switches from the correct answer back to a
 * chip with no way back".
 *
 * Null where the question asks for no uncertainty, which is the same gate the facet and the
 * evaluator read.
 */
function uncertaintyOf(data, ctx) {
    if (!uncertaintyAsked(data))
        return null;
    return valueOf(data.uncertaintyValue, ctx) ?? valueOf(data.uncertaintyEquation, ctx);
}
/** A numerical sample with its uncertainty, where the question asks for one. */
function withUncertainty(base, data, ctx) {
    const u = uncertaintyOf(data, ctx);
    return u === null ? base : { ...base, uncertainty: u };
}
/**
 * How far from the answer a `mistake` may stray, as a multiple of the answer's own size.
 *
 * Calibrated against two cases that pull in opposite directions. A tight tolerance wants a
 * value well down the ladder — `10 ± 2%` reads as a mistake at 13.6, nine rungs out — while a
 * loose one reaches absurdity almost immediately, since `10 ± 50%` is already at 100 by the
 * same rung. Capping by the ANSWER'S magnitude rather than by rung count serves both: the
 * tight band keeps its ninth rung, the loose one stops at 40.
 *
 * An answer of zero has no magnitude to multiply, so the cap does not apply and the ladder's
 * own end stands.
 */
const MISTAKE_MAX_RELATIVE = 5;
/**
 * Pick one rung of a ladder, by tier.
 *
 * The ladders differ in kind — Numerical tiles by its band, Slider and Tilt step across a fixed
 * domain, Compass walks a dial and wraps, GPS escalates in metres — but all are ordered
 * NEAREST FIRST, so "first rejected" and "furthest still sensible" mean the same thing on each.
 */
function pickRejected(candidates, accepts, tier, anchor) {
    const rejected = candidates.filter(c => !accepts(c)).map(c => Math.round(c * 1e6) / 1e6);
    if (rejected.length === 0)
        return null;
    if (tier === 'near')
        return rejected[0];
    const limit = anchor !== undefined && Math.abs(anchor) > 0
        ? Math.abs(anchor) * MISTAKE_MAX_RELATIVE
        : Number.POSITIVE_INFINITY;
    // Furthest that stays sensible; the near miss itself if nothing further does, in which case
    // the two tiers coincide and the caller drops one — see `answerChoicesFor`.
    const within = rejected.filter(v => Math.abs(v) <= limit);
    return (within.length > 0 ? within[within.length - 1] : rejected[0]);
}
/**
 * WRONG VALUES FOR AN UNBOUNDED BLOCK, stepped by the teacher's own tolerance.
 *
 * Numerical was the odd one out. Slider, Tilt, Compass and Rotation all step outward from the
 * answer by a fraction of their own domain, so their wrong values scale with the question.
 * Numerical has no domain to divide — a number block accepts any number — so it used a
 * constant: `[top + 1, top + 10, top * 2 + 1]`. A `+1` that never consults the band is wrong at
 * both ends of the scale, not just one:
 *
 *   - `0.001 ± 2%` offered **1.00102**, a thousand times the answer;
 *   - `1000000 ± 2%` offered **1020001**, one part in a million past the edge and visually
 *     indistinguishable from the boundary.
 *
 * The band is the scale that was missing. `valueInBucket` tiles outward in band-widths, which is
 * the same measure `signatureForBands` uses to decide whether two attempts were the same idea —
 * so a value offered here as a mistake is one the attempt counter also calls a different idea.
 *
 * Nearest first, because the first value the block rejects is the near miss a teacher is writing
 * their incorrect feedback for — the ordering `outward` already argues for at length.
 */
const WRONG_STEPS = [1, 2, 3, 5, 9];
/*
 * ON A GRID A PERSON WOULD READ, for the reason `outward` states: a wrong answer is a value the
 * teacher quotes, not an intermediate. `10 ± 0.2` tiles to 10.399999999999999 in binary floating
 * point, and six decimals of residue standing where `10.4` belongs misrepresents the number.
 * Rounded at the point of generation, so the value the predicate tests is the value returned —
 * rounding afterwards could nudge a rejected value back inside the band.
 */
function onGrid(value, scale) {
    const dp = Math.min(12, Math.max(0, Math.ceil(-Math.log10(Math.abs(scale) || 1)) + 2));
    return Math.round(value * 10 ** dp) / 10 ** dp;
}
function wrongCandidatesFromBand(band) {
    const anchor = band.at ?? (band.lo + band.hi) / 2;
    const tiled = WRONG_STEPS
        .map(k => valueInBucket(band, k))
        .filter((v) => v !== null);
    if (tiled.length > 0)
        return tiled.map(v => onGrid(v, v - anchor));
    /*
     * A ZERO-WIDTH BAND — tolerance 0, or none authored — has no width to tile by, and that is a
     * teacher who declared no closeness at all rather than a mistake. The only scale left is the
     * ANSWER'S OWN SIZE, which is still proportional and so still right at both ends: a tenth of
     * 0.001 is 0.0001, a tenth of a million is a hundred thousand. A fixed floor would have put
     * the old `+1` bug straight back for the small answers it was worst for.
     *
     * An answer of exactly zero has no magnitude to scale by, and 1 is the only honest step.
     */
    const step = Math.abs(anchor) > 0 ? Math.abs(anchor) / 10 : 1;
    return WRONG_STEPS.map(k => onGrid(anchor + k * step, step));
}
/**
 * Values to try, NEAREST THE RIGHT ANSWER FIRST and then outward across the domain.
 *
 * Order is the whole of the difference between a useful wrong answer and a useless one. A
 * plain sweep from the bottom of the range returns the bottom of the range: a slider whose
 * answer is 50 offered 0, which is outside the band and reads as nobody's answer. Stepping
 * outward from the answer offers the nearest value the block rejects, which is the near miss
 * a teacher is writing their incorrect feedback for.
 *
 * It still reaches the whole domain, so a band covering most of the range is still escaped —
 * that was the reason for sweeping in the first place.
 */
function outward(from, lo, hi, steps = 60, prefer) {
    if (!Number.isFinite(lo) || !Number.isFinite(hi) || hi <= lo)
        return [];
    const step = (hi - lo) / steps;
    /*
     * ON A GRID A PERSON WOULD READ, because a wrong answer is a value the teacher quotes in a
     * sentence and not an intermediate. A 0-100 slider divides into steps of 1.666…, so the
     * first value past a ±10 band came out as 61.666667 — six decimals of arithmetic residue
     * standing where "62" belongs. Reported from the owner's own content, which is the same
     * complaint `fmtDegrees` was written for: precision that is noise misrepresents the thing
     * being measured.
     *
     * The grid comes from the STEP, so it is as fine as the sweep is and no finer: a 0-100
     * range rounds to whole numbers, a 0-1 range to hundredths. Rounding happens HERE and not
     * on the way out, so the value the predicate accepts or rejects is the value returned —
     * rounding afterwards could nudge a rejected value back inside the band and hand back an
     * answer the block would mark correct.
     */
    const dp = Math.max(0, Math.ceil(-Math.log10(step)));
    const grid = (v) => Math.round(v * 10 ** dp) / 10 ** dp;
    const out = [];
    /*
     * `prefer` PUTS ONE SIDE FIRST, for the one caller with a reason to care.
     *
     * A dual slider's handles cannot cross, so a wrong value for the SECOND handle has to be
     * sought upward and for the first downward, or the pair shown is a state the slider refuses
     * to enter. The other side is still offered, after everything on the preferred one, so a
     * handle with no room of its own still finds a wrong value rather than none.
     *
     * Undefined keeps the original interleaving exactly, which is what every other caller
     * wants: a lone number has no neighbour to stay clear of.
     */
    const push = (into, v) => { if (v >= lo && v <= hi)
        into.push(v); };
    const other = [];
    for (let i = 1; i <= steps; i++) {
        const up = grid(from + step * i);
        const down = grid(from - step * i);
        if (prefer === undefined) {
            push(out, up);
            push(out, down);
            continue;
        }
        push(out, prefer === 'down' ? down : up);
        push(other, prefer === 'down' ? up : down);
    }
    /*
     * THE PREFERRED SIDE ALONE WHERE IT HAS ROOM — not the preferred side followed by the
     * other, which is how this was written first and is wrong for the `mistake` tier.
     * `pickRejected` takes the LAST candidate for `mistake`, so appending the far side put the
     * furthest wrong value at the bottom of the track: handle 2's "clearly wrong" answer came
     * back as 20 on a slider whose first handle sat at 20. Exactly the crossing this parameter
     * exists to prevent, reintroduced one layer up.
     *
     * The fallback only applies where the preferred side has nothing at all — a handle already
     * against its end of the track — and there, a wrong value on the other side beats none.
     */
    return out.length > 0 ? out : other;
}
/** The same, on a dial: outward from the answer and wrapped, so the far side is reached last. */
function outwardOnDial(from, steps = 72) {
    const out = [];
    for (let i = 1; i <= steps / 2; i++) {
        const d = (360 / steps) * i;
        out.push((from + d + 360) % 360, (from - d + 360) % 360);
    }
    return out;
}
/** The answer rows of a block that keeps them as `{ value, error* }`, with formulas resolved. */
function resolvedAnswers(data, ctx) {
    const raw = Array.isArray(data.answers) ? data.answers : [];
    const num = (v) => valueOf(v, ctx) ?? 0;
    return raw
        .map(a => {
        const e = a;
        return {
            id: String(e.id ?? ''),
            value: valueOf(e.value, ctx),
            toleranceValue: num(e.toleranceValue),
            toleranceAbove: num(e.toleranceAbove),
            toleranceBelow: num(e.toleranceBelow),
            /*
             * CARRIED, because tolerance is PER ANSWER and this function is the pipe every sampler
             * drinks from.
             *
             * Dropping them was silent and total. `computeAcceptedRanges` reads both correctly, via
             * `answerHalfWidths(a, config)` and `modeForAnswer(a, config)` — but it was handed
             * answers these two fields had been stripped off, so every read fell through to the
             * block-level legacy scope and the per-answer setting did nothing at all. A teacher
             * mixing an absolute band with a percentage one got the block's default for both, and
             * every tier the chip offers was drawn against the wrong band.
             *
             * `undefined` rather than a default when absent: that is what makes the legacy fallback
             * inside those readers fire for a block authored before the move. Substituting a value
             * here would overwrite the block's setting with a guess.
             */
            toleranceSymmetry: e.toleranceSymmetry,
            /*
             * Typed as the two-value union rather than `string`, because that is what the accepted
             * range configs declare and a wider type is rejected at every one of them. The three
             * angle blocks have no percentage mode — there being no privileged zero on a bearing —
             * and simply never carry the field, so the union costs them nothing.
             */
            toleranceMode: e.toleranceMode,
        };
    })
        .filter((a) => a.value !== null);
}
/** The between rows, likewise. All four blocks spell them `low`/`high`. */
function resolvedBetween(data, ctx) {
    return (Array.isArray(data.betweenAnswers) ? data.betweenAnswers : []).map(b => {
        const e = b;
        return { id: String(e.id ?? ''), low: valueOf(e.low, ctx) ?? 0, high: valueOf(e.high, ctx) ?? 0 };
    });
}
/**
 * ONE WRONG ANSWER PER TYPE, each asked of the block that grades it.
 *
 * Returns the RECORDED shape, like every other sampler here, so a caller can hand it straight
 * to `resolveAnswerRef`. `null` where there is no answer authored yet, or where the block
 * accepts its whole domain and so has no wrong answer to offer.
 */
function wrongAnswerFor(blockType, data, ctx, tier = 'near') {
    const answers = resolvedAnswers(data, ctx);
    const betweenAnswers = resolvedBetween(data, ctx);
    const conditionType = data.conditionType ?? 'exact';
    const toleranceSymmetry = data.toleranceSymmetry ?? 'symmetric';
    const threshold = valueOf(data.threshold, ctx) ?? 0;
    const facet = ctx.facet;
    switch (blockType) {
        case 'numerical': {
            /*
             * A REFERENCE TO THE UNIT NEEDS A WRONG UNIT, not a wrong number.
             *
             * The wrong sample keeps the canonical unit, because a unit belongs to the quantity.
             * Read through the `unit` facet that put the CORRECT unit on screen under a cross —
             * the teacher's own answer marked as a mistake. So this facet offers a unit the block
             * itself marks wrong, and offers nothing at all when the teacher has authored none:
             * there is no wrong unit to show, and inventing one would be a different lie.
             */
            if (facet === 'unit') {
                const wrongUnit = (Array.isArray(data.unitOptions) ? data.unitOptions : [])
                    .find(o => o.role === 'wrong');
                const label = wrongUnit?.label;
                if (typeof label !== 'string' || label.trim() === '')
                    return null;
                const value = firstAnswerValue(data, ctx);
                return value === null
                    ? null
                    : withUncertainty({ value, unit: label }, data, ctx);
            }
            /*
             * AND A REFERENCE TO THE UNCERTAINTY NEEDS A WRONG UNCERTAINTY — the same rule as the
             * unit above, and as a dual slider's second handle one block over.
             *
             * Moving the VALUE and calling it wrong is true of the block and useless to a chip
             * naming the error box: that one then shows its correct number under a cross. So the
             * uncertainty moves and the value stays right.
             *
             * Outside the band the block itself grades with. At one significant figure that band
             * is a single value — "the rounding convention is the band" — so the first step out is
             * already wrong, which is why this works without a tolerance being set.
             */
            if (facet === ANSWER_FACET_UNCERTAINTY) {
                const value = firstAnswerValue(data, ctx);
                const correct = uncertaintyOf(data, ctx);
                if (value === null || correct === null)
                    return null;
                const band = uncertaintyBand(correct, data);
                /* Up and down from the correct one, in steps of its own size: an uncertainty has no
                   domain to tile, and it is the only scale it brings with it. */
                const step = Math.abs(correct) > 0 ? Math.abs(correct) : 1;
                const candidates = tier === 'near'
                    ? [correct + step * 0.5, correct - step * 0.5, correct + step, correct - step]
                    : [correct * 3, correct * 0.25, correct + step * 4];
                const wrong = candidates
                    .map(c => cleanComputed(c))
                    .find(c => c > 0 && !uncertaintyMatches(c, band.lo, band.hi));
                return wrong === undefined
                    ? null
                    : { ...withUnit(value, data), uncertainty: wrong };
            }
            if (answers.length === 0 && betweenAnswers.length === 0)
                return null;
            const config = {
                conditionType, answers, betweenAnswers, threshold, toleranceSymmetry,
                toleranceMode: data.toleranceMode ?? 'absolute',
            };
            /*
             * UNBOUNDED, so there is no domain to step across — the BAND is the scale instead. See
             * `wrongCandidatesFromBand`. A threshold condition (`< 5`) has no finite band to tile, so
             * it keeps the blunt fallback: there is nothing to be proportional to.
             */
            const ranges = computeAcceptedRanges(config);
            const tileable = ranges.filter(r => Number.isFinite(r.lo) && Number.isFinite(r.hi));
            const widest = tileable.length
                ? tileable.reduce((a, b) => (b.hi > a.hi ? b : a))
                : null;
            const candidates = widest ? wrongCandidatesFromBand(widest) : [0, 1, -1];
            const anchor = widest ? (widest.lo + widest.hi) / 2 : undefined;
            const wrong = pickRejected(candidates, v => isWithinAcceptedRange(v, config), tier, anchor);
            /* The uncertainty stays CORRECT here: this tier is about a wrong value, and a chip
               quoting the error box beside it must not read as wrong too. */
            return wrong === null ? null : withUncertainty(withUnit(wrong, data), data, ctx);
        }
        case 'tilt': {
            if (answers.length === 0 && betweenAnswers.length === 0)
                return null;
            const config = { conditionType, answers, betweenAnswers, toleranceSymmetry, threshold };
            const from = answers[0]?.value ?? 0;
            return pickRejected(outward(from, TILT_DOMAIN_LO, TILT_DOMAIN_HI), v => isTiltWithinAcceptedRange(v, config), tier, from);
        }
        case 'compass': {
            if (answers.length === 0 && betweenAnswers.length === 0)
                return null;
            const config = { conditionType, answers, betweenAnswers, toleranceSymmetry };
            /*
             * A CIRCLE, and the one place "just past the edge" misleads: a band that straddles 0 is
             * accepted from both sides, so a value nudged past one edge can land back inside
             * through the other. Walking outward and wrapping cannot.
             */
            /* No cap on a dial: `outwardOnDial` already stops at the antipode, which is as far from
               the answer as a heading can be. A relative limit would be meaningless on a circle. */
            const heading = pickRejected(outwardOnDial(answers[0]?.value ?? 0), v => isCompassWithinAcceptedRange(v, config), tier);
            return heading === null ? null : { headingDegrees: heading };
        }
        case 'slider': {
            const lo = valueOf(data.min, ctx);
            const hi = valueOf(data.max, ctx);
            if (lo === null || hi === null)
                return null;
            const correct = sampleCorrectAnswer('slider', data, ctx);
            if (!correct?.a)
                return null;
            const dual = !!data.dualHandle;
            /*
             * WHICH HANDLES MOVE — and `combined` moves BOTH, which it did not.
             *
             * The rule was `facet === 'high'`, so a chip quoting the whole RANGE only ever saw its
             * first number change: every tier read "12 to 80", "15 to 80", "3 to 80". Reported as
             * "for a combined slider, only the first value ever varies".
             *
             * A reference to one END moves that end and leaves the other correct, which is what
             * makes it read as a near miss rather than as nonsense. A reference to the whole RANGE
             * is about the range, so the range is what moves.
             */
            const wantsB = dual && (facet === ANSWER_FACET_HIGH || facet === ANSWER_FACET_COMBINED);
            const wantsA = !dual || facet !== ANSWER_FACET_HIGH;
            /*
             * AND NEITHER MAY CROSS THE OTHER.
             *
             * The block forbids it — "slider 1 is always at or below slider 2", enforced on every
             * drag, with `sliderAnswersCrossedVerdict` refusing an authored pair that crosses. The
             * sampler did not know, so it offered a preview of a state the slider cannot enter:
             * handle 2 walked DOWN from its answer and, where that answer came from a
             * between-condition with no exact value, started at the slider's floor and sat on 0.
             * Reported as "for handle 2, the value hits 0 ... the slider handles are not allowed to
             * cross, so it's unlikely that it'll ever be on the low range".
             *
             * So each handle walks AWAY from the other and is bounded by where the other sits.
             */
            const setupFor = (source, own) => {
                const ans = own ? answers : resolvedAnswers(source, ctx);
                const btw = own ? betweenAnswers : resolvedBetween(source, ctx);
                return {
                    ok: ans.length > 0 || btw.length > 0,
                    /* The walk starts at the handle's OWN answer. Where it has none — a between with no
                       exact value — it starts at the MIDDLE of its accepted band, not at the end of the
                       track, which is where `?? lo` used to put handle 2. */
                    from: ans[0]?.value ?? (btw[0] ? (btw[0].low + btw[0].high) / 2 : undefined),
                    config: {
                        conditionType: source.conditionType ?? 'exact',
                        answers: ans,
                        betweenAnswers: btw,
                        threshold: valueOf(source.threshold, ctx) ?? 0,
                        toleranceSymmetry: source.toleranceSymmetry ?? 'symmetric',
                        toleranceMode: source.toleranceMode ?? 'absolute',
                    },
                };
            };
            const A = setupFor(data, true);
            const B = dual
                ? setupFor(data.handleB ?? {}, false)
                : null;
            const aFrom = A.from ?? correct.a.value;
            const bFrom = B?.from ?? correct.b?.value;
            /* A first, downward-preferring and never above where B sits — which leaves B's floor
               below its own answer whatever A did, so the two walks cannot deadlock. */
            let aOut = correct.a.value;
            let movedA = null;
            if (wantsA && A.ok) {
                const ceiling = dual && bFrom !== undefined ? Math.min(hi, bFrom) : hi;
                movedA = pickRejected(outward(aFrom, lo, ceiling, 60, dual ? 'down' : undefined), v => isSliderWithinAcceptedRange(v, A.config), tier, aFrom);
                if (movedA !== null)
                    aOut = movedA;
            }
            if (!dual)
                return movedA === null ? null : { a: { value: aOut }, b: undefined };
            let bOut = correct.b?.value;
            let movedB = null;
            if (wantsB && B?.ok && bFrom !== undefined) {
                movedB = pickRejected(outward(bFrom, Math.max(lo, aOut), hi, 60, 'up'), v => isSliderWithinAcceptedRange(v, B.config), tier, bFrom);
                if (movedB !== null)
                    bOut = movedB;
            }
            /* Nothing moved means there is no wrong answer to show, rather than the correct pair
               offered under a cross. */
            if (movedA === null && movedB === null)
                return null;
            if (bOut === undefined)
                return { a: { value: aOut }, b: undefined };
            /*
             * NO SORT AT THE EXIT, deliberately — the bounds above are the guarantee.
             *
             * A first version swapped the two when they came out crossed, which looks like belt and
             * braces and is worse than nothing: it keeps the PAIR legal by relabelling which handle
             * is which, so a chip quoting `high` would print handle 1's correct value under handle
             * 2's name. It also made the crossing test unable to see a crossing walk at all — the
             * walk could cross freely and the sort tidied it up before any assertion ran.
             *
             * The walks cannot cross: A is ceilinged at where B starts and B is floored at where A
             * ended. If that ever stops being true the test says so, which a sort would prevent.
             */
            return { a: { value: aOut }, b: { value: bOut } };
        }
        case 'gps': {
            const lat = valueOf(data.targetLat, ctx);
            const lng = valueOf(data.targetLng, ctx);
            if (lat === null || lng === null)
                return null;
            /*
             * A DISTANCE, not a range — and the HALF the reference reads has to be the half that
             * moves. A point moved only in latitude is a wrong fix, and a reference to its
             * longitude then showed the correct number under a cross.
             *
             * Measured from the TOLERANCE and not from the target, or a kilometre-wide radius
             * swallows the offset. The block's own haversine confirms the point really is outside.
             */
            const radius = valueOf(data.toleranceRadius, ctx) ?? 0;
            const METRES_PER_DEGREE = 111_320;
            const moveLat = facet !== 'lng';
            const moveLng = facet !== 'lat';
            for (const extra of [50, 500, 5_000]) {
                const shift = (radius + extra) / METRES_PER_DEGREE;
                const moved = {
                    lat: moveLat ? lat + shift : lat,
                    // Longitude degrees shorten with latitude, so the shift is widened to cover it.
                    lng: moveLng ? lng + shift / Math.max(0.05, Math.cos((lat * Math.PI) / 180)) : lng,
                };
                if (haversineDistanceMeters({ lat, lng }, moved) > radius) {
                    return {
                        lat: Math.round(moved.lat * 1e6) / 1e6,
                        lng: Math.round(moved.lng * 1e6) / 1e6,
                    };
                }
            }
            return null;
        }
        default:
            return null;
    }
}
/**
 * A DIFFERENT CORRECT ANSWER — accepted, but not the one the teacher typed.
 *
 * The tier that catches an equation only working for the authored value. A downstream formula
 * reading `[their answer]` and quietly depending on it being exactly 10 is wrong for every
 * student who answers 9.9, and nothing else on this bench would reveal it: the correct sample
 * IS 10, so the equation passes.
 *
 * Half a half-width off the answer, which is inside the band by construction and far enough
 * from the edge that rounding cannot push it out. A zero-width band has no second accepted
 * value and returns null, which drops the tier rather than offering the same number twice.
 *
 * Only the blocks whose answer is a single number on a line. A GPS fix is a coordinate pair and
 * a slider may have two handles; "another accepted value" is a different construction for each,
 * and offering a wrong shape would be worse than offering one tier fewer.
 */
function otherCorrectAnswer(blockType, data, ctx) {
    if (blockType !== 'numerical' && blockType !== 'tilt' && blockType !== 'compass')
        return null;
    const answers = resolvedAnswers(data, ctx);
    const first = answers[0];
    if (!first || !Number.isFinite(first.value))
        return null;
    /*
     * A CHIP NAMING THE UNCERTAINTY ASKS ABOUT THE UNCERTAINTY'S BAND, not the value's.
     *
     * This answered with a shifted VALUE whatever the chip quoted, and dropped the uncertainty
     * on the way out — so cycling an uncertainty chip onto this tier produced a sample with no
     * uncertainty in it, the facet resolved to nothing, and the cycle died on a placeholder.
     *
     * "there is no tolerance, so the answer either is what the teacher set or is not" — which
     * is exactly what falls out of asking the right band: at one significant figure
     * `uncertaintyBand` is a single value, so there is no SECOND accepted uncertainty and this
     * tier is simply absent. The same as any other number with no tolerance.
     */
    if (blockType === 'numerical' && ctx.facet === ANSWER_FACET_UNCERTAINTY) {
        const correct = uncertaintyOf(data, ctx);
        if (correct === null)
            return null;
        const { lo, hi } = uncertaintyBand(correct, data);
        if (!(hi > lo))
            return null;
        const other = cleanComputed((correct + hi) / 2);
        return other === correct || !uncertaintyMatches(other, lo, hi)
            ? null
            : { ...withUnit(first.value, data), uncertainty: other };
    }
    // Through the bridge, not a hand-rolled ternary: see `answer-tolerance`. Both of this
    // module's own copies read the BLOCK, which is the setting the per-answer move retired.
    const legacy = data;
    const { below, above } = answerHalfWidths(first, legacy);
    const half = Math.max(Math.abs(above), Math.abs(below));
    const pct = answerToleranceMode(first, legacy, 'absolute') === 'percentage';
    const width = pct ? Math.abs(first.value) * half / 100 : half;
    if (!(width > 0))
        return null;
    const shifted = Math.round((first.value + width / 2) * 1e6) / 1e6;
    if (shifted === first.value)
        return null;
    return blockType === 'numerical' ? withUncertainty(withUnit(shifted, data), data, ctx)
        : blockType === 'compass' ? { headingDegrees: shifted }
            : shifted;
}
/**
 * ANOTHER ANSWER OF THE SAME KIND — what Random means for a recalled answer.
 *
 * Random rerolls the variables, and a recall in the same equation should move with them. But
 * the chip's cycle has ALREADY said what kind of answer is being tested: a teacher who set it
 * to a plausible mistake to walk an error-propagation pathway must not have that undone every
 * time they reroll. So Random varies the number and leaves the KIND alone.
 *
 * The kind needs no state to track, because the value carries it. `bucketIn` says how far
 * wrong a value is in units of the teacher's own tolerance, so the bucket the current value
 * falls in IS the tier the chip is showing, and redrawing means drawing again inside that
 * bucket's span. One exception, which is the only thing the value cannot say for itself: a
 * value equal to an authored answer is `exact` rather than merely inside the band, and the
 * honest redraw there is a DIFFERENT authored answer — or the same one, when there is only
 * one, because "a different exact answer" does not exist.
 *
 * `draw` is injected rather than calling `Math.random` so a caller can seed it; the test does.
 */
export function redrawInSameTier(current, answers, band, draw = Math.random) {
    const authored = answers.filter(Number.isFinite);
    if (authored.some(a => a === current)) {
        const others = authored.filter(a => a !== current);
        return others.length === 0 ? current : others[Math.floor(draw() * others.length)];
    }
    const bucket = bucketIn(current, band);
    if (bucket === null)
        return null;
    const span = bucketSpan(band, bucket);
    if (span === null)
        return null;
    const width = span.hi - span.lo;
    // On the grid the rest of this module uses, for the reason it gives: a sampled answer is a
    // value a teacher reads, and arithmetic residue misrepresents the number being shown.
    const value = onGrid(span.lo + draw() * width, width);
    /*
     * THE TILES SHARE THEIR EDGES, and bucket 0 wins the one it shares.
     *
     * `bucketIn` tests the accepted band INCLUSIVELY before it tiles, because a value sitting
     * exactly on an endpoint is one the grader accepts. So the bottom of bucket 1's span is the
     * top of the band, and a draw landing there comes back as CORRECT — a near miss redrawn
     * into a pass, which is the one thing this function exists to prevent. Found by the test,
     * at `draw() === 0`, where it is reached every time rather than one roll in a thousand.
     *
     * Falls back to the tile's own representative, which is in the tile by construction, rather
     * than nudging by a grid step: the grid is derived from the span, so a step is not
     * guaranteed to clear the boundary on a narrow tile.
     */
    if (bucketIn(value, band) !== bucket)
        return onGrid(valueInBucket(band, bucket), width);
    return value;
}
/**
 * REROLL ONE BLOCK'S RECORDED ANSWER, inside the tier it is already in.
 *
 * The block-shaped wrapper around `redrawInSameTier`: it finds the band, reads the number out
 * of whatever shape that block records, draws, and puts the number back in the same shape.
 * Lives here beside `sampleCorrectAnswer` and `wrongAnswerFor` because knowing that Compass
 * records `{ headingDegrees }` and Numerical records `{ value, unit }` is this module's job
 * and nobody else's.
 *
 * The three blocks whose answer is ONE NUMBER ON A LINE. A slider may have two handles and a
 * GPS answer is a coordinate pair; "the same kind of wrong, somewhere else" is a different
 * construction for each, and returning null leaves their value alone rather than inventing a
 * shape. Same line `otherCorrectAnswer` draws, for the same reason.
 */
export function rerollAnswerInTier(blockType, data, ctx, current, draw = Math.random) {
    if (blockType !== 'numerical' && blockType !== 'tilt' && blockType !== 'compass')
        return null;
    const answers = resolvedAnswers(data, ctx);
    if (answers.length === 0)
        return null;
    const currentNumber = blockType === 'compass'
        ? current?.headingDegrees
        : blockType === 'numerical'
            ? current?.value
            : current;
    if (typeof currentNumber !== 'number' || !Number.isFinite(currentNumber))
        return null;
    // The band the current value is measured against: the one it is nearest, which is what
    // `signatureForBands` means by attribution and the only sane reading with several answers.
    const legacy = data;
    const bands = answers
        .filter(a => Number.isFinite(a.value))
        .map(a => {
        // Per answer, and read through the bridge for the reason `answer-tolerance` gives: two
        // answers under OR logic are independent windows and may disagree about both settings.
        const w = answerHalfWidths(a, legacy);
        const above = Math.abs(w.above);
        const below = Math.abs(w.below);
        const pct = answerToleranceMode(a, legacy, 'absolute') === 'percentage';
        const hi = a.value + (pct ? Math.abs(a.value) * above / 100 : above);
        const lo = a.value - (pct ? Math.abs(a.value) * below / 100 : below);
        return { lo, hi, at: a.value };
    });
    if (bands.length === 0)
        return null;
    const band = bands.reduce((best, b) => Math.abs(currentNumber - b.at) < Math.abs(currentNumber - best.at) ? b : best);
    const next = redrawInSameTier(currentNumber, bands.map(b => b.at), band, draw);
    if (next === null)
        return null;
    return blockType === 'numerical' ? withUnit(next, data)
        : blockType === 'compass' ? { headingDegrees: next }
            : next;
}
/** Spelled out because this module must not import the facet table for one comparison. */
const ANSWER_FACET_HIGH = 'high';
const ANSWER_FACET_UNCERTAINTY = 'uncertainty';
const ANSWER_FACET_COMBINED = 'combined';
const plain = (html) => String(html ?? '').replace(/<[^>]*>/g, '').trim();
export function answerChoicesFor(blockType, data, ctx) {
    switch (blockType) {
        case 'mcq':
        case 'dropdown':
            return resolvedEntries(data, 'options', ctx)
                .filter(e => plain(e.text) !== '')
                .map(e => ({
                id: e.id,
                label: plain(e.text),
                kind: e.role,
                sample: { selectedTexts: [String(e.text)] },
            }));
        case 'keyword':
            return [
                ...resolvedEntries(data, 'options', ctx)
                    .filter(e => plain(e.text) !== '')
                    .map(e => ({ id: e.id, label: plain(e.text), kind: e.role, sample: String(e.text) })),
                // Keyword matches text, so "none of them" is a real and common answer — and the one
                // a teacher is most likely to be writing feedback for.
                { id: '__other', kind: 'open', sample: null },
            ];
        case 'scale':
            return resolvedEntries(data, 'values', ctx).map(e => ({
                id: e.id,
                // Scale labels its points; the value is the fallback for one left unnamed.
                label: plain(e.label) || String(e.value ?? e.id),
                kind: e.role,
                sample: asNumber(e.value),
            }));
        /*
         * THE OPEN BLOCKS. A number, a heading, a position: the correct one stands for itself and
         * everything else is a value only the teacher can choose, so the list is two entries and
         * the second has no sample. Their feedback bands and branches would each be a class too,
         * and are left out until there is a control that can show them usefully.
         */
        /*
         * EVERY BLOCK THAT ANSWERS WITH A NUMBER AGAINST A TOLERANCE — Numerical, Slider, Tilt,
         * Compass, GPS. Accepted and not accepted are the two classes anyone writing the
         * surrounding sentence cares about, and a tolerance means the second always exists.
         *
         * `wrongAnswerFor` asks each block's own predicate rather than restating five lots of
         * tolerance arithmetic here. The open entry stays: a SPECIFIC wrong value is still the
         * teacher's to choose, and only they know which one their feedback is about.
         */
        case 'numerical':
        case 'slider':
        case 'tilt':
        case 'compass':
        case 'gps': {
            /*
             * FIVE CLASSES, NOT TWO, because a teacher reading a recalled answer inside an equation
             * is asking a finer question than "does this pass". `exact` is the value they authored;
             * `correct` is a DIFFERENT accepted value, which is what catches an equation that only
             * works for the answer itself; `near` is just outside; `mistake` is clearly outside but
             * still a number a student could produce.
             *
             * Entries collapse rather than duplicate. A zero-width band has no second correct value,
             * and a band wide enough that one rung out is already the furthest sensible value has no
             * second wrong one — in both cases the tier is simply absent, and the chip offers four
             * steps instead of five. `cyclable` already counts what is offered rather than assuming.
             */
            const exact = sampleCorrectAnswer(blockType, data, ctx);
            const alt = otherCorrectAnswer(blockType, data, ctx);
            const near = wrongAnswerFor(blockType, data, ctx, 'near');
            const mistake = wrongAnswerFor(blockType, data, ctx, 'mistake');
            const same = (a, b) => JSON.stringify(a) === JSON.stringify(b);
            return [
                ...(exact === null ? [] : [{ id: '__exact', kind: 'correct', tier: 'exact', sample: exact }]),
                ...(alt === null || same(alt, exact) ? [] : [{ id: '__correct', kind: 'correct', tier: 'correct', sample: alt }]),
                ...(near === null ? [] : [{ id: '__wrong', kind: 'incorrect', tier: 'near', sample: near }]),
                ...(mistake === null || same(mistake, near) ? [] : [{ id: '__mistake', kind: 'incorrect', tier: 'mistake', sample: mistake }]),
                { id: '__other', kind: 'open', tier: 'open', sample: null },
            ];
        }
        /*
         * FITB enumerates like an option block, one blank at a time. The reference names a blank
         * — the facet IS the blank id — so the answers to cycle are that blank's accepted words,
         * and every other blank keeps its own correct word so the rest of the passage still reads.
         */
        case 'fitb': {
            const base = sampleCorrectAnswer('fitb', data, ctx);
            const blanks = Array.isArray(data.blanks) ? data.blanks : [];
            const blank = blanks.find(b => b.id === ctx.facet);
            if (!blank || !Array.isArray(blank.words)) {
                return [
                    ...(base === null ? [] : [{ id: '__correct', kind: 'correct', sample: base }]),
                    { id: '__other', kind: 'open', sample: null },
                ];
            }
            return [
                ...blank.words
                    .filter(w => plain(w.text) !== '')
                    .map(w => ({
                    id: w.id,
                    label: plain(w.text),
                    kind: (w.role === 'correct' ? 'correct' : w.role === 'incorrect' ? 'incorrect' : 'neutral'),
                    sample: { ...(base ?? {}), [blank.id]: String(w.text) },
                })),
                { id: '__other', kind: 'open', sample: null },
            ];
        }
        default:
            return [];
    }
}
//# sourceMappingURL=answer-sample.js.map